import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserProvider, useUser } from './context/UserContext';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import SendMoney from './pages/SendMoney';
import ReceiveMoney from './pages/ReceiveMoney';
import Withdraw from './pages/Withdraw';
import History from './pages/History';
import Settings from './pages/Settings';
import Referral from './pages/Referral';
import WalletID from './pages/WalletID';
import WalletDetail from './pages/WalletDetail';
import SendCrypto from './pages/SendCrypto';
import Account from './pages/Account';
import Certifications from './pages/Certifications';
import PaymentLink from './pages/PaymentLink';
import PeneInvestment from './pages/PeneInvestment';
import PeneInvestmentDashboard from './pages/PeneInvestmentDashboard';
import PeneExchange from './pages/PeneExchange';
import PeneTransfer from './pages/PeneTransfer';
import PeneInvestmentPlans from './pages/PeneInvestmentPlans';
import AccountVerification from './pages/AccountVerification';
import ConnectAccount from './pages/ConnectAccount';
import UpdateAccount from './pages/UpdateAccount';
import ChangePin from './pages/ChangePin';
import ConfirmSubscription from './pages/ConfirmSubscription';
import EmailVerification from './pages/EmailVerification';
import Deposit from './pages/Deposit';
import Notifications from './pages/Notifications';
import AdminDashboard from './pages/AdminDashboard';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useUser();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useUser();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : children;
};

function App() {
  return (
    <UserProvider>
      <AppRoutes />
    </UserProvider>
  );
}

function AppRoutes() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/certifications" element={<Certifications />} />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/send" 
            element={
              <ProtectedRoute>
                <SendMoney />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/receive" 
            element={
              <ProtectedRoute>
                <ReceiveMoney />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/withdraw" 
            element={
              <ProtectedRoute>
                <Withdraw />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/deposit" 
            element={
              <ProtectedRoute>
                <Deposit />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/history" 
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/referral" 
            element={
              <ProtectedRoute>
                <Referral />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/wallet" 
            element={
              <ProtectedRoute>
                <WalletID />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/wallet/:cryptoType" 
            element={
              <ProtectedRoute>
                <WalletDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/wallet/:cryptoType/send" 
            element={
              <ProtectedRoute>
                <SendCrypto />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/payment-link" 
            element={
              <ProtectedRoute>
                <PaymentLink />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/investment" 
            element={
              <ProtectedRoute>
                <PeneInvestment />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/investment-dashboard" 
            element={
              <ProtectedRoute>
                <PeneInvestmentDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/exchange" 
            element={
              <ProtectedRoute>
                <PeneExchange />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/transfer" 
            element={
              <ProtectedRoute>
                <PeneTransfer />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/investment-plans" 
            element={
              <ProtectedRoute>
                <PeneInvestmentPlans />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/account-verification" 
            element={
              <ProtectedRoute>
                <AccountVerification />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/connect-account" 
            element={
              <ProtectedRoute>
                <ConnectAccount />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/update-account" 
            element={
              <ProtectedRoute>
                <UpdateAccount />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/change-pin" 
            element={
              <ProtectedRoute>
                <ChangePin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/confirm-subscription" 
            element={
              <ProtectedRoute>
                <ConfirmSubscription />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/email-verification" 
            element={
              <ProtectedRoute>
                <EmailVerification />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Catch all - redirect to dashboard or landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

export default App;
